import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { ReplaySubject, filter, takeUntil } from 'rxjs';

import { fileDownloader } from 'app/utils/file-download-helper';
import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import SocialFile from 'entity/SocialFile';

import { SocialFileService } from '../../services/social-file/social-file.service';
import { socialAppSelectors } from '../../state/app';

interface SelectableFiles extends SocialFile {
  isSelected: boolean;
}

@Component({
  selector: 'app-file-library',
  templateUrl: './file-library.component.html',
  styleUrl: './file-library.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileLibraryComponent extends Loggable implements OnInit, OnDestroy {
  private store = inject(Store);
  private socialFileService = inject(SocialFileService);

  private destroyed$ = new ReplaySubject<boolean>(1);

  private readonly roomSetId = signal('');
  protected readonly isLoading = signal(false);
  protected readonly files = signal<SelectableFiles[]>([]);

  public constructor() {
    super();
    this.store
      .select(socialAppSelectors.getCurrentSocial)
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((social) => {
        this.roomSetId.set(social.socialRoomSetId ?? '');
      });
  }

  public ngOnInit() {
    void this.getFilesOnThisRoomSet();
  }

  private async getFilesOnThisRoomSet() {
    await this.socialFileService
      .getFiles(this.roomSetId())
      .then((files) => {
        this.files.set(files.map((file) => ({ ...file, isSelected: false })));
      })
      .catch((err) => this.error(err));
  }

  public changeSelection() {
    if (!this.files().length) {
      return;
    }
    if (this.isSelectedAll) {
      this.removeSelectedFiles();
      return;
    }
    this.selectAllFiles();
  }

  private selectAllFiles() {
    this.files.set(this.files().map((file) => ({ ...file, isSelected: true })));
  }

  private removeSelectedFiles() {
    this.files.set(this.files().map((file) => ({ ...file, isSelected: false })));
  }

  public startDownload() {
    const selectedFiles = this.files().filter((file) => file.isSelected);

    if (!selectedFiles.length) {
      this.info('Select files to download');
      return;
    }

    const filesIds = selectedFiles.flatMap((file) => file.socialFileId ?? '');

    this.downloadFiles(filesIds).catch((err) => this.error(err));
  }

  private async downloadFiles(filesIds: string[]) {
    this.isLoading.set(true);
    await this.socialFileService
      .getDownloadURL(this.roomSetId(), filesIds)
      .then(async (url) => {
        await fileDownloader(url, this.files().find((file) => file.socialFileId === filesIds[0])?.fileName ?? '');
        this.removeSelectedFiles();
      })
      .catch((err) => this.error(err))
      .finally(() => this.isLoading.set(false));
  }

  public get isSelectedAll() {
    const selectedFiles = this.files().filter((file) => file.isSelected);
    return selectedFiles.length === this.files().length;
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
